import React, { Component } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { request } from "app/network";
import { uploadToS3 } from "app/network/api/s3Upload";
import Swal from "sweetalert2";
import { MaterialUI } from "context";
import ImageCropModal from "../ImageCropModal/ImageCropModal";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface AddInterstitialAdModalProps {
    open: boolean;
    editingAd?: any | null;
    onClose: () => void;
    onSuccess: () => void;
    darkMode?: boolean;
    theme?: any;
}

interface AddInterstitialAdModalState {
    title: string;
    description: string;
    imageUrl: string;
    imageUrlMobile: string;
    linkUrl: string;
    linkType: string;
    displayOrder: number;
    isActive: boolean;
    startDate: string;
    endDate: string;
    minDisplayInterval: number;
    maxDailyDisplay: number;
    skipDelaySeconds: number;
    autoCloseSeconds: number;
    loading: boolean;
    darkMode: boolean;
    imageFile: File | null;
    imagePreview: string;
    isDragging: boolean;
    cropModalOpen: boolean;
    tempImageForCrop: string;
    isPermanent: boolean;
    advertiserId: number | null;
    advertisers: Array<{ advertiserId: number; advertiserName: string }>;
    linkTypes: Array<{ code: string; name: string; description: string }>;
    existingAds: Array<{ seqId: number; title: string; displayOrder: number }>;
    newAdPosition: number;
}

class AddInterstitialAdModalClass extends Component<AddInterstitialAdModalProps, AddInterstitialAdModalState> {
    static contextType = MaterialUI;

    constructor(props: AddInterstitialAdModalProps) {
        super(props);
        const initialDarkMode = props.darkMode ?? false;

        this.state = {
            title: "",
            description: "",
            imageUrl: "",
            imageUrlMobile: "",
            linkUrl: "",
            linkType: "INTERSTITIAL_NONE",
            displayOrder: 0,
            isActive: true,
            startDate: "",
            endDate: "",
            minDisplayInterval: 0,
            maxDailyDisplay: 999,
            skipDelaySeconds: 3,
            autoCloseSeconds: 0,
            loading: false,
            darkMode: initialDarkMode,
            imageFile: null,
            imagePreview: "",
            isDragging: false,
            cropModalOpen: false,
            tempImageForCrop: "",
            isPermanent: false,
            advertiserId: null,
            advertisers: [],
            linkTypes: [],
            existingAds: [],
            newAdPosition: 0,
        };
    }


    async componentDidMount() {
        // darkMode 초기화
        const context = this.context as any;
        const initialDarkMode = this.props.darkMode ?? context?.darkMode ?? false;
        this.setState({ darkMode: initialDarkMode });

        // 광고주 목록 불러오기
        try {
            const param = {
                PageNumber: 1,
                PageSize: 1000, // 모든 광고주 가져오기
            };

            console.log("[AddInterstitialAdModal] Fetching advertisers with param:", param);
            const response = await request("Advertiser.pagedList", { paramz: param });
            console.log("[AddInterstitialAdModal] Advertiser API response:", response);

            if (response && response.data && response.data.items) {
                const advertisers = response.data.items.map((advertiser: any) => ({
                    advertiserId: advertiser.seqId,
                    advertiserName: advertiser.companyName || advertiser.name || "이름 없음",
                }));
                console.log("[AddInterstitialAdModal] Mapped advertisers:", advertisers);

                // 광고주가 있으면 첫 번째 광고주 자동 선택
                const defaultAdvertiserId = advertisers.length > 0 ? advertisers[0].advertiserId : null;
                console.log("[AddInterstitialAdModal] Auto-selecting first advertiser:", defaultAdvertiserId);

                this.setState({
                    advertisers,
                    advertiserId: defaultAdvertiserId
                });
            } else {
                console.log("[AddInterstitialAdModal] No advertiser data found");
            }
        } catch (error) {
            console.error("[AddInterstitialAdModal] 광고주 목록 불러오기 실패:", error);
        }

        // 링크 타입 목록 불러오기
        try {
            console.log("[AddInterstitialAdModal] Fetching link types...");
            const response = await request("CommonCode.getByGroup", {
                paramz: {},
                pathParams: { groupCode: "INTERSTITIAL_LINK_TYPE" },
            });

            console.log("[AddInterstitialAdModal] Link type API response:", response);

            if (response && response.data && response.data.length > 0) {
                const linkTypes = response.data
                    .filter((item: any) => item.isActive)
                    .map((item: any) => ({
                        code: item.code,
                        name: item.name || item.codeName,
                        description: item.description || "",
                    }));

                console.log("[AddInterstitialAdModal] Mapped link types:", linkTypes);
                this.setState({ linkTypes });
            } else {
                // API에서 데이터가 없으면 기본값 사용
                console.log("[AddInterstitialAdModal] No link types from API, using defaults");
                this.setState({
                    linkTypes: [
                        { code: "INTERSTITIAL_NONE", name: "링크 없음", description: "클릭 시 아무 동작 없음" },
                        { code: "INTERSTITIAL_WEB_LINK", name: "웹 링크", description: "외부 웹사이트로 이동" },
                        { code: "INTERSTITIAL_PHONE_CALL", name: "전화 걸기", description: "전화번호로 전화 걸기" },
                    ]
                });
            }
        } catch (error) {
            console.error("[AddInterstitialAdModal] 링크 타입 목록 불러오기 실패:", error);
            // 에러 발생 시에도 기본값 사용
            this.setState({
                linkTypes: [
                    { code: "INTERSTITIAL_NONE", name: "링크 없음", description: "클릭 시 아무 동작 없음" },
                    { code: "INTERSTITIAL_WEB_LINK", name: "웹 링크", description: "외부 웹사이트로 이동" },
                    { code: "INTERSTITIAL_PHONE_CALL", name: "전화 걸기", description: "전화번호로 전화 걸기" },
                ]
            });
        }

        // 기존 광고 목록 불러오기 (표시 순서 관리용)
        this.loadExistingAds();
    }

    componentDidUpdate(prevProps: AddInterstitialAdModalProps) {
        const context = this.context as any;
        const currentDarkMode = this.props.darkMode ?? context?.darkMode ?? false;

        if (currentDarkMode !== this.state.darkMode) {
            this.setState({ darkMode: currentDarkMode });
        }

        if (this.props.open && !prevProps.open) {
            // 모달이 열릴 때 기존 광고 목록 다시 로드
            this.loadExistingAds();

            // 편집 모드인 경우 기존 데이터로 초기화
            if (this.props.editingAd) {
                const ad = this.props.editingAd;
                this.setState({
                    title: ad.title || "",
                    description: ad.description || "",
                    imageUrl: ad.imageUrl || "",
                    imageUrlMobile: ad.imageUrlMobile || "",
                    linkUrl: ad.linkUrl || "",
                    linkType: ad.linkType || "INTERSTITIAL_NONE",
                    displayOrder: ad.displayOrder || 0,
                    isActive: ad.isActive !== undefined ? ad.isActive : true,
                    startDate: ad.startDate || "",
                    endDate: ad.endDate || "",
                    minDisplayInterval: ad.minIntervalMin || 0,
                    maxDailyDisplay: ad.maxDisplayCount || 999,
                    skipDelaySeconds: ad.skipDelaySec || 3,
                    autoCloseSeconds: ad.autoCloseSec || 0,
                    advertiserId: ad.advertiserSeqId || null,
                    isPermanent: ad.isPermanent || false,
                    loading: false,
                    darkMode: currentDarkMode,
                    imageFile: null,
                    imagePreview: ad.imageUrl || "",
                    isDragging: false,
                    cropModalOpen: false,
                    tempImageForCrop: "",
                });
            } else {
                // 추가 모드인 경우 초기화
                this.setState({
                    title: "",
                    description: "",
                    imageUrl: "",
                    imageUrlMobile: "",
                    linkUrl: "",
                    linkType: "INTERSTITIAL_NONE",
                    displayOrder: 0,
                    isActive: true,
                    startDate: "",
                    endDate: "",
                    minDisplayInterval: 0,
                    maxDailyDisplay: 999,
                    skipDelaySeconds: 3,
                    autoCloseSeconds: 0,
                    loading: false,
                    darkMode: currentDarkMode,
                    imageFile: null,
                    imagePreview: "",
                    isDragging: false,
                    cropModalOpen: false,
                    tempImageForCrop: "",
                    isPermanent: false,
                    newAdPosition: this.state.existingAds.length, // 기본적으로 마지막 위치
                });
            }
        }
    }

    loadExistingAds = async () => {
        try {
            console.log("[AddInterstitialAdModal] Fetching existing ads...");

            // 로그인 시 선택한 테넌트 가져오기
            const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
            const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);

            const response = await request("InterstitialAd.pagedList", {
                paramz: {
                    PageNumber: 1,
                    PageSize: 100,
                    IsActive: true, // 활성화된 광고만
                    TenantSeqId: savedTenantId ? parseInt(savedTenantId, 10) : undefined,
                },
            });

            console.log("[AddInterstitialAdModal] Existing ads API response:", response);

            if (response && response.data && response.data.items) {
                const existingAds = response.data.items
                    .map((ad: any) => ({
                        seqId: ad.seqId,
                        title: ad.title || "제목 없음",
                        displayOrder: ad.displayOrder || 0,
                    }))
                    .sort((a: any, b: any) => a.displayOrder - b.displayOrder);

                console.log("[AddInterstitialAdModal] Mapped existing ads:", existingAds);

                // 새 광고는 기본적으로 마지막 위치에 배치
                this.setState({
                    existingAds,
                    newAdPosition: existingAds.length,
                });
            }
        } catch (error) {
            console.error("[AddInterstitialAdModal] 기존 광고 목록 불러오기 실패:", error);
        }
    };

    handleChange = (field: keyof AddInterstitialAdModalState, value: any) => {
        this.setState({ [field]: value } as any);
    };

    resizeImage = (file: File, maxWidth: number = 900, maxHeight: number = 1600): Promise<File> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    // 9:16 비율 유지
                    const aspectRatio = 9 / 16;
                    let width = img.width;
                    let height = img.height;

                    // 최대 너비를 초과하면 리사이즈
                    if (width > maxWidth) {
                        width = maxWidth;
                        height = width / aspectRatio;
                    }

                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('커버스 컨텍스트를 가져올 수 없습니다'));
                        return;
                    }

                    ctx.drawImage(img, 0, 0, width, height);

                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                reject(new Error('이미지 변환에 실패했습니다'));
                                return;
                            }
                            const resizedFile = new File([blob], file.name, {
                                type: file.type,
                                lastModified: Date.now(),
                            });
                            resolve(resizedFile);
                        },
                        file.type,
                        0.9 // 품질 90%
                    );
                };
                img.onerror = () => reject(new Error('이미지 로드에 실패했습니다'));
                img.src = e.target?.result as string;
            };
            reader.onerror = () => reject(new Error('파일 읽기에 실패했습니다'));
            reader.readAsDataURL(file);
        });
    };

    handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // 이미지 파일 타입 검증
            const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!validImageTypes.includes(file.type)) {
                this.showAlert(
                    "파일 형식 오류",
                    "지원되는 이미지 형식: JPG, PNG, GIF, WebP",
                    "error"
                );
                event.target.value = '';
                return;
            }

            // 해상도 기반 자동 리사이즈 (900x1600 초과 시)
            const img = new Image();
            const reader = new FileReader();
            let processedFile = file;

            reader.onloadend = async () => {
                const result = reader.result as string;

                img.onload = async () => {
                    // 9:16 비율 검증 (전면광고용)
                    const aspectRatio = img.width / img.height;
                    const targetRatio = 9 / 16; // 0.5625
                    const tolerance = 0.02; // 2% 허용 오차

                    if (Math.abs(aspectRatio - targetRatio) > tolerance) {
                        // 9:16 비율이 아니면 크롭 모달 열기
                        this.setState({
                            cropModalOpen: true,
                            tempImageForCrop: result,
                        });
                        event.target.value = '';
                        return;
                    }

                    // 900x1600 초과 시 자동 리사이즈
                    if (img.width > 900 || img.height > 1600) {
                        try {
                            this.showAlert(
                                "이미지 자동 리사이즈",
                                `이미지 해상도가 900x1600을 초과하여 자동으로 리사이즈합니다. (원본: ${img.width}x${img.height})`,
                                "info"
                            );
                            processedFile = await this.resizeImage(file, 900, 1600);
                        } catch (error) {
                            this.showAlert(
                                "이미지 리사이즈 실패",
                                `이미지 리사이즈 중 오류가 발생했습니다.`,
                                "error"
                            );
                            event.target.value = '';
                            return;
                        }
                    }

                    // 검증 통과 시 미리보기 설정
                    this.setState({
                        imageFile: processedFile,
                        imagePreview: result,
                    });
                };

                img.src = result;
            };

            reader.readAsDataURL(file);
        }
    };

    handleCropComplete = async (croppedFile: File) => {
        // 크롭된 이미지를 처리
        const reader = new FileReader();
        reader.onloadend = async () => {
            const result = reader.result as string;
            const img = new Image();

            img.onload = async () => {
                let processedFile = croppedFile;

                // 900x1600 초과 시 자동 리사이즈
                if (img.width > 900 || img.height > 1600) {
                    try {
                        this.showAlert(
                            "이미지 자동 리사이즈",
                            `이미지 해상도가 900x1600을 초과하여 자동으로 리사이즈합니다. (원본: ${img.width}x${img.height})`,
                            "info"
                        );
                        processedFile = await this.resizeImage(croppedFile, 900, 1600);
                    } catch (error) {
                        this.showAlert(
                            "이미지 리사이즈 실패",
                            `이미지 리사이즈 중 오류가 발생했습니다.`,
                            "error"
                        );
                        return;
                    }
                }

                this.setState({
                    imageFile: processedFile,
                    imagePreview: result,
                });
            };

            img.src = result;
        };

        reader.readAsDataURL(croppedFile);
    };

    handleCropCancel = () => {
        this.setState({
            cropModalOpen: false,
            tempImageForCrop: "",
        });
    };

    handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ isDragging: true });
    };

    handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ isDragging: false });
    };

    handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ isDragging: false });

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];

            // 동일한 검증 로직 사용
            const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!validImageTypes.includes(file.type)) {
                this.showAlert(
                    "파일 형식 오류",
                    "지원되는 이미지 형식: JPG, PNG, GIF, WebP",
                    "error"
                );
                return;
            }

            // 해상도 기반 자동 리사이즈 (900x1600 초과 시)
            const img = new Image();
            const reader = new FileReader();
            let processedFile = file;

            reader.onloadend = async () => {
                const result = reader.result as string;

                img.onload = async () => {
                    // 9:16 비율 검증 (전면광고용)
                    const aspectRatio = img.width / img.height;
                    const targetRatio = 9 / 16; // 0.5625
                    const tolerance = 0.02; // 2% 허용 오차

                    if (Math.abs(aspectRatio - targetRatio) > tolerance) {
                        // 9:16 비율이 아니면 크롭 모달 열기
                        this.setState({
                            cropModalOpen: true,
                            tempImageForCrop: result,
                        });
                        return;
                    }

                    // 900x1600 초과 시 자동 리사이즈
                    if (img.width > 900 || img.height > 1600) {
                        try {
                            this.showAlert(
                                "이미지 자동 리사이즈",
                                `이미지 해상도가 900x1600을 초과하여 자동으로 리사이즈합니다. (원본: ${img.width}x${img.height})`,
                                "info"
                            );
                            processedFile = await this.resizeImage(file, 900, 1600);
                        } catch (error) {
                            this.showAlert(
                                "이미지 리사이즈 실패",
                                `이미지 리사이즈 중 오류가 발생했습니다.`,
                                "error"
                            );
                            return;
                        }
                    }

                    this.setState({
                        imageFile: processedFile,
                        imagePreview: result,
                    });
                };

                img.src = result;
            };

            reader.readAsDataURL(file);
        }
    };

    handlePaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            // 이미지 타입만 처리
            if (item.type.indexOf('image') !== -1) {
                e.preventDefault();
                const file = item.getAsFile();
                if (!file) continue;

                // handleDrop과 동일한 로직 사용
                const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
                if (!validImageTypes.includes(file.type)) {
                    this.showAlert(
                        "파일 형식 오류",
                        "지원되는 이미지 형식: JPG, PNG, GIF, WebP",
                        "error"
                    );
                    return;
                }

                // 해상도 기반 자동 리사이즈 (900x1600 초과 시)
                const img = new Image();
                const reader = new FileReader();
                let processedFile = file;

                reader.onloadend = async () => {
                    const result = reader.result as string;

                    img.onload = async () => {
                        // 9:16 비율 검증 (전면광고용)
                        const aspectRatio = img.width / img.height;
                        const targetRatio = 9 / 16; // 0.5625
                        const tolerance = 0.02; // 2% 허용 오차

                        if (Math.abs(aspectRatio - targetRatio) > tolerance) {
                            // 9:16 비율이 아니면 크롭 모달 열기
                            this.setState({
                                cropModalOpen: true,
                                tempImageForCrop: result,
                            });
                            return;
                        }

                        // 900x1600 초과 시 자동 리사이즈
                        if (img.width > 900 || img.height > 1600) {
                            try {
                                this.showAlert(
                                    "이미지 자동 리사이즈",
                                    `이미지 해상도가 900x1600을 초과하여 자동으로 리사이즈합니다. (원본: ${img.width}x${img.height})`,
                                    "info"
                                );
                                processedFile = await this.resizeImage(file, 900, 1600);
                            } catch (error) {
                                this.showAlert(
                                    "이미지 리사이즈 실패",
                                    `이미지 리사이즈 중 오류가 발생했습니다.`,
                                    "error"
                                );
                                return;
                            }
                        }

                        this.setState({
                            imageFile: processedFile,
                            imagePreview: result,
                        });
                    };

                    img.src = result;
                };

                reader.readAsDataURL(file);
                break; // 첫 번째 이미지만 처리
            }
        }
    };

    handleSubmit = async () => {
        const { title, imageUrl, imageFile, isActive, displayOrder, startDate, endDate,
            minDisplayInterval, maxDailyDisplay, skipDelaySeconds, autoCloseSeconds, advertiserId } = this.state;

        // 필수 값 체크
        if (!advertiserId) {
            this.showAlert("입력 오류", "광고주를 선택해주세요.", "error");
            return;
        }

        if (!title.trim()) {
            this.showAlert("입력 오류", "광고 제목을 입력해주세요.", "error");
            return;
        }

        if (!imageUrl.trim() && !imageFile) {
            this.showAlert("입력 오류", "이미지 URL을 입력하거나 이미지 파일을 업로드해주세요.", "error");
            return;
        }

        this.setState({ loading: true });

        try {
            // 이미지 파일이 있으면 S3에 업로드
            let finalImageUrl = imageUrl;
            if (imageFile) {
                try {
                    const uploadResult = await uploadToS3(imageFile, "ads", (progress) => {
                        console.log(`업로드 진행률: ${progress}%`);
                        // TODO: 진행률 표시 UI 추가 가능
                    });
                    finalImageUrl = uploadResult.url;
                } catch (uploadError: any) {
                    this.setState({ loading: false });
                    this.showAlert("업로드 실패", uploadError.message || "이미지 업로드에 실패했습니다.", "error");
                    return;
                }
            }

            // 표시 순서 계산
            let calculatedDisplayOrder = 1;
            if (this.props.editingAd) {
                // 편집 모드: 기존 displayOrder 유지
                calculatedDisplayOrder = this.props.editingAd.displayOrder || 1;
            } else if (this.state.existingAds.length > 0) {
                // 추가 모드: newAdPosition을 기반으로 displayOrder 결정
                calculatedDisplayOrder = this.state.newAdPosition + 1;
            }

            // 로그인 시 선택한 테넌트 가져오기
            const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
            const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);

            const param = {
                TenantSeqId: savedTenantId ? parseInt(savedTenantId, 10) : undefined,
                Title: title,
                Description: this.state.description || null,
                ImageUrl: finalImageUrl,
                ImageUrlMobile: this.state.imageUrlMobile || null,
                LinkUrl: this.state.linkUrl || null,
                LinkType: this.state.linkType,
                DisplayOrder: calculatedDisplayOrder,
                IsActive: isActive,
                StartDate: startDate && startDate.trim() !== "" && startDate !== "-" ? startDate : null,
                EndDate: endDate && endDate.trim() !== "" && endDate !== "-" ? endDate : null,
                MinDisplayInterval: minDisplayInterval,
                MaxDailyDisplay: maxDailyDisplay,
                SkipDelaySeconds: skipDelaySeconds,
                AutoCloseSeconds: autoCloseSeconds,
                AdvertiserSeqId: advertiserId,
            };

            if (this.props.editingAd) {
                // 편집 모드: update API 호출
                const res = await request("InterstitialAd.update", {
                    paramz: param,
                    pathParams: { id: this.props.editingAd.seqId.toString() }
                });

                if (res && res.data) {
                    this.showAlert("성공", "전면광고가 수정되었습니다.", "success");
                    this.props.onSuccess();
                    this.props.onClose();
                } else {
                    throw new Error("수정 실패");
                }
            } else {
                // 추가 모드: create API 호출
                const res = await request("InterstitialAd.create", { paramz: param });

                if (res && res.data) {
                    this.showAlert("성공", "전면광고가 등록되었습니다.", "success");
                    this.props.onSuccess();
                    this.props.onClose();
                } else {
                    throw new Error("등록 실패");
                }
            }
        } catch (error: any) {
            console.error("전면광고 처리 실패:", error);
            this.showAlert("오류", error.message || "전면광고 처리에 실패했습니다.", "error");
        } finally {
            this.setState({ loading: false });
        }
    };

    showAlert = (title: string, text: string, icon: "success" | "error" | "warning" | "info") => {
        const { darkMode } = this.state;
        Swal.fire({
            title,
            text,
            icon,
            confirmButtonText: "확인",
            background: darkMode ? "#1a2035" : "#fff",
            color: darkMode ? "#fff" : "#000",
            customClass: {
                container: 'swal-high-z-index'
            },
            didOpen: () => {
                // SweetAlert2 컨테이너의 z-index를 Dialog보다 높게 설정
                const swalContainer = document.querySelector('.swal-high-z-index') as HTMLElement;
                if (swalContainer) {
                    swalContainer.style.zIndex = '9999';
                }
            }
        });
    };

    render() {
        const { open, onClose } = this.props;
        const { title, description, imageUrl, imageUrlMobile, linkUrl, displayOrder, isActive,
            startDate, endDate, minDisplayInterval, maxDailyDisplay, skipDelaySeconds,
            autoCloseSeconds, loading, darkMode, imagePreview, isDragging, cropModalOpen, tempImageForCrop, isPermanent, advertiserId } = this.state;

        return (
            <React.Fragment>
                <Dialog
                    open={open}
                    onClose={onClose}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            backgroundColor: darkMode ? "#1a2035" : "#fff",
                            color: darkMode ? "#fff" : "#000",
                        },
                    }}
                >
                    <DialogTitle
                        sx={{
                            color: darkMode ? '#ffffff' : 'inherit',
                        }}
                    >
                        {this.props.editingAd ? '전면광고 수정' : '전면광고 추가'}
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            backgroundColor: darkMode ? '#1a2035' : '#fff',
                        }}
                    >
                        <MDBox pt={2}>
                            <Grid container spacing={3}>
                                {/* 광고주 선택 */}
                                <Grid item xs={12}>
                                    <MDBox>
                                        <MDTypography variant="caption" fontWeight="regular" sx={{ color: darkMode ? "rgba(255, 255, 255, 0.8)" : "#344767" }} mb={0.5}>
                                            광고주 선택 *
                                        </MDTypography>
                                        <Select
                                            fullWidth
                                            value={this.state.advertiserId || ""}
                                            onChange={(e) => this.handleChange("advertiserId", e.target.value as number)}
                                            displayEmpty
                                            sx={{
                                                height: '45px',
                                                color: darkMode ? "#fff" : "#344767",
                                                backgroundColor: darkMode ? "transparent" : "transparent",
                                                fontSize: '0.875rem',
                                                '.MuiOutlinedInput-notchedOutline': {
                                                    borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "#d2d6da",
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: darkMode ? "rgba(255, 255, 255, 0.4)" : "#d2d6da",
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: darkMode ? "#49a3f1" : "#1976d2",
                                                    borderWidth: '2px',
                                                },
                                                '.MuiSvgIcon-root': {
                                                    color: darkMode ? "#fff" : "#344767",
                                                },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        backgroundColor: darkMode ? "#1a2035" : "#fff",
                                                        color: darkMode ? "#fff" : "#344767",
                                                        maxHeight: 300,
                                                    },
                                                },
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                <em style={{ color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)" }}>
                                                    광고주를 선택하세요
                                                </em>
                                            </MenuItem>
                                            {this.state.advertisers.map((advertiser) => (
                                                <MenuItem
                                                    key={advertiser.advertiserId}
                                                    value={advertiser.advertiserId}
                                                    sx={{
                                                        color: darkMode ? "#fff" : "#344767",
                                                        fontSize: '0.875rem',
                                                        '&:hover': {
                                                            backgroundColor: darkMode ? "rgba(73, 163, 241, 0.15)" : "rgba(25, 118, 210, 0.08)",
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: darkMode ? "rgba(73, 163, 241, 0.2)" : "rgba(25, 118, 210, 0.12)",
                                                        },
                                                        '&.Mui-selected:hover': {
                                                            backgroundColor: darkMode ? "rgba(73, 163, 241, 0.3)" : "rgba(25, 118, 210, 0.16)",
                                                        },
                                                    }}
                                                >
                                                    {advertiser.advertiserName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </MDBox>
                                </Grid>

                                {/* 제목 */}
                                <Grid item xs={12}>
                                    <MDInput
                                        label="광고 제목 *"
                                        fullWidth
                                        value={this.state.title}
                                        onChange={(e: any) => this.handleChange("title", e.target.value)}
                                        InputLabelProps={{
                                            style: { color: darkMode ? "#fff" : "#000" },
                                        }}
                                        sx={{
                                            '& .MuiInputBase-input': {
                                                color: darkMode ? '#ffffff' : 'inherit',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: darkMode ? '#1a2035' : '#fff',
                                                '& fieldset': {
                                                    borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'inherit',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* 설명 */}
                                <Grid item xs={12}>
                                    <MDInput
                                        label="설명"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={this.state.description}
                                        onChange={(e: any) => this.handleChange("description", e.target.value)}
                                        InputLabelProps={{
                                            style: { color: darkMode ? "#fff" : "#000" },
                                        }}
                                        sx={{
                                            '& .MuiInputBase-input': {
                                                color: darkMode ? '#ffffff' : 'inherit',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: darkMode ? '#1a2035' : '#fff',
                                                '& fieldset': {
                                                    borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'inherit',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* 이미지 업로드 */}
                                <Grid item xs={12}>
                                    <MDBox mb={1}>
                                        <MDTypography variant="button" fontWeight="medium" color="text">
                                            광고 이미지 *
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox
                                        onDragEnter={this.handleDragEnter}
                                        onDragLeave={this.handleDragLeave}
                                        onDragOver={this.handleDragOver}
                                        onDrop={this.handleDrop}
                                        onPaste={this.handlePaste}
                                        tabIndex={0}
                                        sx={{
                                            border: this.state.isDragging
                                                ? darkMode ? "2px solid #49a3f1" : "2px solid #1976d2"
                                                : darkMode ? "1px dashed rgba(255, 255, 255, 0.3)" : "1px dashed rgba(0, 0, 0, 0.3)",
                                            borderRadius: "8px",
                                            p: 2,
                                            textAlign: "center",
                                            backgroundColor: this.state.isDragging
                                                ? darkMode ? "rgba(73, 163, 241, 0.1)" : "rgba(25, 118, 210, 0.05)"
                                                : "transparent",
                                            transition: "all 0.2s ease-in-out",
                                            outline: "none",
                                        }}
                                    >
                                        <input
                                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                            style={{ display: 'none' }}
                                            id="image-upload-input"
                                            type="file"
                                            onChange={this.handleImageChange}
                                        />
                                        {!imagePreview && (
                                            <>
                                                <label htmlFor="image-upload-input">
                                                    <Button
                                                        component="span"
                                                        variant="outlined"
                                                        startIcon={<CloudUploadIcon />}
                                                        sx={{
                                                            color: darkMode ? "#fff" : "#000",
                                                            borderColor: darkMode ? "#fff" : "#000",
                                                            "&:hover": {
                                                                borderColor: darkMode ? "#49a3f1" : "#1976d2",
                                                                backgroundColor: darkMode ? "rgba(73, 163, 241, 0.1)" : "rgba(25, 118, 210, 0.1)",
                                                            },
                                                        }}
                                                    >
                                                        이미지 선택
                                                    </Button>
                                                </label>
                                                <MDTypography
                                                    variant="caption"
                                                    color="text"
                                                    sx={{
                                                        display: "block",
                                                        mt: 1,
                                                        opacity: 0.7,
                                                    }}
                                                >
                                                    또는 이미지를 드래그하거나 Ctrl+V로 붙여넣기
                                                </MDTypography>
                                            </>
                                        )}

                                        {imagePreview && (
                                            <MDBox mt={2}>
                                                <MDBox position="relative" display="inline-block">
                                                    <img
                                                        src={imagePreview}
                                                        alt="미리보기"
                                                        style={{
                                                            maxWidth: "100%",
                                                            maxHeight: "300px",
                                                            borderRadius: "8px",
                                                            border: darkMode ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid rgba(0, 0, 0, 0.2)",
                                                        }}
                                                    />
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="error"
                                                        onClick={() => {
                                                            this.setState({ imageFile: null, imagePreview: "" });
                                                            const input = document.getElementById('image-upload-input') as HTMLInputElement;
                                                            if (input) input.value = '';
                                                        }}
                                                        sx={{
                                                            position: "absolute",
                                                            top: 8,
                                                            right: 8,
                                                            minWidth: "auto",
                                                            px: 1,
                                                            backgroundColor: "#f44336",
                                                            color: "#ffffff",
                                                            "&:hover": {
                                                                backgroundColor: "#d32f2f",
                                                            },
                                                        }}
                                                    >
                                                        삭제
                                                    </Button>
                                                </MDBox>
                                                <MDTypography variant="caption" color="text" display="block" mt={1}>
                                                    {this.state.imageFile?.name} ({(this.state.imageFile?.size! / 1024).toFixed(2)} KB)
                                                </MDTypography>
                                            </MDBox>
                                        )}

                                        <MDTypography variant="caption" sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" }} display="block" mt={1}>
                                            지원 형식: JPG, PNG, GIF, WebP
                                            <br />
                                            비율: 9:16 세로 화면 (권장: 900x1600, 720x1280)
                                            <br />
                                            900x1600 초과 시 자동 리사이즈됩니다
                                            {!imagePreview && (
                                                <>
                                                    <br />
                                                    파일을 드래그하여 놓거나 버튼을 클릭하세요
                                                </>
                                            )}
                                        </MDTypography>
                                    </MDBox>
                                </Grid>

                                {/* 이미지 URL (직접 입력) - 이미지가 선택되지 않았을 때만 표시 */}
                                {!imagePreview && (
                                    <Grid item xs={12}>
                                        <MDInput
                                            label="또는 이미지 URL 직접 입력"
                                            fullWidth
                                            value={this.state.imageUrl}
                                            onChange={(e: any) => this.handleChange("imageUrl", e.target.value)}
                                            InputLabelProps={{
                                                style: { color: darkMode ? "#fff" : "#000" },
                                            }}
                                            FormHelperTextProps={{
                                                style: { color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" },
                                            }}
                                            helperText="이미지 파일을 업로드하거나 URL을 입력하세요"
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    color: darkMode ? '#ffffff' : 'inherit',
                                                },
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: darkMode ? '#1a2035' : '#fff',
                                                    '& fieldset': {
                                                        borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'inherit',
                                                    },
                                                },
                                            }}
                                        />
                                    </Grid>
                                )}

                                {/* 링크 타입 선택 */}
                                <Grid item xs={12}>
                                    <MDBox>
                                        <MDTypography variant="caption" fontWeight="regular" sx={{ color: darkMode ? "rgba(255, 255, 255, 0.8)" : "#344767" }} mb={0.5}>
                                            링크 방법
                                        </MDTypography>
                                        <Select
                                            fullWidth
                                            value={this.state.linkType}
                                            onChange={(e) => this.handleChange("linkType", e.target.value)}
                                            sx={{
                                                height: '45px',
                                                color: darkMode ? "#fff" : "#344767",
                                                fontSize: '0.875rem',
                                                '.MuiOutlinedInput-notchedOutline': {
                                                    borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "#d2d6da",
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: darkMode ? "rgba(255, 255, 255, 0.4)" : "#d2d6da",
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: darkMode ? "#49a3f1" : "#1976d2",
                                                    borderWidth: '2px',
                                                },
                                                '.MuiSvgIcon-root': {
                                                    color: darkMode ? "#fff" : "#344767",
                                                },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        backgroundColor: darkMode ? "#1a2035" : "#fff",
                                                        color: darkMode ? "#fff" : "#344767",
                                                        maxHeight: 300,
                                                    },
                                                },
                                            }}
                                        >
                                            {this.state.linkTypes.map((linkType) => (
                                                <MenuItem
                                                    key={linkType.code}
                                                    value={linkType.code}
                                                    sx={{
                                                        color: darkMode ? "#fff" : "#344767",
                                                        fontSize: '0.875rem',
                                                        '&:hover': {
                                                            backgroundColor: darkMode ? "rgba(73, 163, 241, 0.15)" : "rgba(25, 118, 210, 0.08)",
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: darkMode ? "rgba(73, 163, 241, 0.2)" : "rgba(25, 118, 210, 0.12)",
                                                        },
                                                        '&.Mui-selected:hover': {
                                                            backgroundColor: darkMode ? "rgba(73, 163, 241, 0.3)" : "rgba(25, 118, 210, 0.16)",
                                                        },
                                                    }}
                                                >
                                                    <MDBox>
                                                        <MDTypography variant="button" fontWeight="medium">
                                                            {linkType.name}
                                                        </MDTypography>
                                                        {linkType.description && (
                                                            <MDTypography variant="caption" sx={{ color: darkMode ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)" }} display="block">
                                                                {linkType.description}
                                                            </MDTypography>
                                                        )}
                                                    </MDBox>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </MDBox>
                                </Grid>

                                {/* 링크 URL */}
                                {this.state.linkType !== "INTERSTITIAL_NONE" && (
                                    <Grid item xs={12}>
                                        <MDInput
                                            label={
                                                this.state.linkType === "PHONE_CALL"
                                                    ? "전화번호"
                                                    : this.state.linkType === "INTERNAL"
                                                        ? "화면 경로"
                                                        : "링크 URL"
                                            }
                                            fullWidth
                                            value={this.state.linkUrl}
                                            onChange={(e: any) => this.handleChange("linkUrl", e.target.value)}
                                            InputLabelProps={{
                                                style: { color: darkMode ? "#fff" : "#000" },
                                            }}
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    color: darkMode ? '#ffffff' : 'inherit',
                                                },
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: darkMode ? '#1a2035' : '#fff',
                                                    '& fieldset': {
                                                        borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'inherit',
                                                    },
                                                },
                                            }}
                                            FormHelperTextProps={{
                                                style: { color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" },
                                            }}
                                            helperText={
                                                this.state.linkType === "PHONE_CALL"
                                                    ? "예: 010-1234-5678"
                                                    : this.state.linkType === "INTERNAL"
                                                        ? "예: /profile, /settings"
                                                        : "예: https://example.com"
                                            }
                                        />
                                    </Grid>
                                )}

                                {/* 표시 순서 - 드래그 앤 드롭 */}
                                <Grid item xs={12}>
                                    {this.state.existingAds.length === 0 ? (
                                        <MDBox>
                                            <MDTypography
                                                variant="caption"
                                                sx={{ color: darkMode ? "rgba(255, 255, 255, 0.8)" : "#344767" }}
                                            >
                                                광고 표시 순서
                                            </MDTypography>
                                            <MDBox
                                                mt={1}
                                                p={2}
                                                sx={{
                                                    borderRadius: "8px",
                                                    backgroundColor: darkMode
                                                        ? "rgba(73, 163, 241, 0.1)"
                                                        : "rgba(25, 118, 210, 0.05)",
                                                    border: darkMode
                                                        ? "1px dashed rgba(73, 163, 241, 0.5)"
                                                        : "1px dashed rgba(25, 118, 210, 0.5)",
                                                }}
                                            >
                                                <MDTypography
                                                    variant="button"
                                                    sx={{
                                                        color: darkMode ? "#49a3f1" : "#1976d2",
                                                    }}
                                                >
                                                    🎉 첫 번째 광고가 됩니다!
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    ) : (
                                        <DisplayOrderManager
                                            existingAds={this.state.existingAds}
                                            newAdPosition={this.state.newAdPosition}
                                            onPositionChange={(newPosition) =>
                                                this.setState({ newAdPosition: newPosition })
                                            }
                                            darkMode={darkMode}
                                        />
                                    )}
                                </Grid>

                                {/* 활성화 */}
                                <Grid item xs={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={this.state.isActive}
                                                onChange={(e) => this.handleChange("isActive", e.target.checked)}
                                            />
                                        }
                                        label="활성화"
                                    />
                                </Grid>

                                {/* 영구 노출 옵션 */}
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={this.state.isPermanent}
                                                onChange={(e) => this.handleChange("isPermanent", e.target.checked)}
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: darkMode ? "#49a3f1" : "#1976d2",
                                                    },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                        backgroundColor: darkMode ? "#49a3f1" : "#1976d2",
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <MDTypography variant="button" color="text">
                                                영구 노출 (시작일/종료일 비활성화)
                                            </MDTypography>
                                        }
                                    />
                                </Grid>

                                {/* 시작일 */}
                                <Grid item xs={6}>
                                    <TextField
                                        label="시작일"
                                        type="datetime-local"
                                        fullWidth
                                        value={this.state.startDate}
                                        onChange={(e: any) => this.handleChange("startDate", e.target.value)}
                                        disabled={this.state.isPermanent}
                                        InputLabelProps={{
                                            shrink: true,
                                            style: { color: darkMode ? "#fff" : "#000" },
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <CalendarTodayIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.8)" : "#344767", opacity: this.state.isPermanent ? 0.5 : 1 }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
                                                filter: darkMode ? 'invert(1)' : 'invert(0)',
                                                opacity: this.state.isPermanent ? 0.5 : 1,
                                            },
                                            '& .MuiInputBase-root': {
                                                backgroundColor: this.state.isPermanent
                                                    ? (darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)')
                                                    : 'transparent',
                                                color: darkMode ? '#fff' : '#000',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : '#d2d6da',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : '#d2d6da',
                                            },
                                            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? '#49a3f1' : '#1976d2',
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* 종료일 */}
                                <Grid item xs={6}>
                                    <TextField
                                        label="종료일"
                                        type="datetime-local"
                                        fullWidth
                                        value={this.state.endDate}
                                        onChange={(e: any) => this.handleChange("endDate", e.target.value)}
                                        disabled={this.state.isPermanent}
                                        InputLabelProps={{
                                            shrink: true,
                                            style: { color: darkMode ? "#fff" : "#000" },
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <CalendarTodayIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.8)" : "#344767", opacity: this.state.isPermanent ? 0.5 : 1 }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
                                                filter: darkMode ? 'invert(1)' : 'invert(0)',
                                                opacity: this.state.isPermanent ? 0.5 : 1,
                                            },
                                            '& .MuiInputBase-root': {
                                                backgroundColor: this.state.isPermanent
                                                    ? (darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)')
                                                    : 'transparent',
                                                color: darkMode ? '#fff' : '#000',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : '#d2d6da',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : '#d2d6da',
                                            },
                                            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? '#49a3f1' : '#1976d2',
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* 최소 노출 간격 (초) */}
                                <Grid item xs={6}>
                                    <MDInput
                                        label="최소 노출 간격 (초)"
                                        type="number"
                                        fullWidth
                                        value={this.state.minDisplayInterval}
                                        onChange={(e: any) => this.handleChange("minDisplayInterval", parseInt(e.target.value) || 0)}
                                        InputLabelProps={{
                                            style: { color: darkMode ? "#fff" : "#000" },
                                        }}
                                        sx={{
                                            '& input[type=number]': {
                                                colorScheme: darkMode ? "dark" : "normal",
                                            },
                                            '& .MuiInputBase-input': {
                                                color: darkMode ? '#ffffff' : 'inherit',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: darkMode ? '#1a2035' : '#fff',
                                                '& fieldset': {
                                                    borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'inherit',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* 일일 최대 노출 */}
                                <Grid item xs={6}>
                                    <MDInput
                                        label="일일 최대 노출 횟수"
                                        type="number"
                                        fullWidth
                                        value={this.state.maxDailyDisplay}
                                        onChange={(e: any) => this.handleChange("maxDailyDisplay", parseInt(e.target.value) || 0)}
                                        InputLabelProps={{
                                            style: { color: darkMode ? "#fff" : "#000" },
                                        }}
                                        sx={{
                                            '& input[type=number]': {
                                                colorScheme: darkMode ? "dark" : "normal",
                                            },
                                            '& .MuiInputBase-input': {
                                                color: darkMode ? '#ffffff' : 'inherit',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: darkMode ? '#1a2035' : '#fff',
                                                '& fieldset': {
                                                    borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'inherit',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* 닫기 버튼 대기 시간 */}
                                <Grid item xs={6}>
                                    <MDInput
                                        label="닫기 버튼 대기 시간 (초)"
                                        type="number"
                                        fullWidth
                                        value={this.state.skipDelaySeconds}
                                        onChange={(e: any) => this.handleChange("skipDelaySeconds", parseInt(e.target.value) || 0)}
                                        InputLabelProps={{
                                            style: { color: darkMode ? "#fff" : "#000" },
                                        }}
                                        sx={{
                                            '& input[type=number]': {
                                                colorScheme: darkMode ? "dark" : "normal",
                                            },
                                            '& .MuiInputBase-input': {
                                                color: darkMode ? '#ffffff' : 'inherit',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: darkMode ? '#1a2035' : '#fff',
                                                '& fieldset': {
                                                    borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'inherit',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* 자동 닫힘 시간 */}
                                <Grid item xs={6}>
                                    <MDInput
                                        label="자동 닫힘 시간 (초)"
                                        type="number"
                                        fullWidth
                                        value={this.state.autoCloseSeconds}
                                        onChange={(e: any) => this.handleChange("autoCloseSeconds", parseInt(e.target.value) || 0)}
                                        InputLabelProps={{
                                            style: { color: darkMode ? "#fff" : "#000" },
                                        }}
                                        sx={{
                                            '& input[type=number]': {
                                                colorScheme: darkMode ? "dark" : "normal",
                                            },
                                            '& .MuiInputBase-input': {
                                                color: darkMode ? '#ffffff' : 'inherit',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: darkMode ? '#1a2035' : '#fff',
                                                '& fieldset': {
                                                    borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'inherit',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </MDBox>
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={onClose} variant="outlined" color="secondary">
                            취소
                        </MDButton>
                        <MDButton
                            onClick={this.handleSubmit}
                            variant="gradient"
                            color="info"
                            disabled={loading}
                        >
                            {loading
                                ? (this.props.editingAd ? "수정 중..." : "등록 중...")
                                : (this.props.editingAd ? "수정" : "등록")
                            }
                        </MDButton>
                    </DialogActions>
                </Dialog>

                {/* 이미지 크롭 모달 */}
                <ImageCropModal
                    open={cropModalOpen}
                    imageSrc={tempImageForCrop}
                    onClose={this.handleCropCancel}
                    onCropComplete={this.handleCropComplete}
                    darkMode={darkMode}
                />
            </React.Fragment>
        );
    }
}

// Sortable Item Component for Drag and Drop
interface SortableItemProps {
    id: string;
    title: string;
    isNewAd: boolean;
    darkMode: boolean;
}

function SortableItem({ id, title, isNewAd, darkMode }: SortableItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <MDBox
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...(isNewAd ? listeners : {})}  // Only apply listeners to new ad
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                mb: 1,
                borderRadius: "8px",
                border: isNewAd
                    ? darkMode
                        ? "2px dashed #49a3f1"
                        : "2px dashed #1976d2"
                    : darkMode
                        ? "1px solid rgba(255, 255, 255, 0.2)"
                        : "1px solid rgba(0, 0, 0, 0.2)",
                backgroundColor: isNewAd
                    ? darkMode
                        ? "rgba(73, 163, 241, 0.1)"
                        : "rgba(25, 118, 210, 0.05)"
                    : darkMode
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.02)",
                cursor: isNewAd ? "grab" : "default",  // Only new ad is draggable
                "&:active": {
                    cursor: isNewAd ? "grabbing" : "default",
                },
            }}
        >
            <DragIndicatorIcon
                sx={{
                    color: isNewAd
                        ? darkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)"
                        : darkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
                }}
            />
            <MDTypography
                variant="button"
                fontWeight={isNewAd ? "bold" : "regular"}
                sx={{
                    color: isNewAd
                        ? darkMode
                            ? "#49a3f1"
                            : "#1976d2"
                        : darkMode
                            ? "rgba(255, 255, 255, 0.8)"
                            : "#344767",
                }}
            >
                {isNewAd ? "🆕 " : ""}{title}
            </MDTypography>
        </MDBox>
    );
}

// Display Order Manager Component
interface DisplayOrderManagerProps {
    existingAds: Array<{ seqId: number; title: string; displayOrder: number }>;
    newAdPosition: number;
    onPositionChange: (newPosition: number) => void;
    darkMode: boolean;
}

function DisplayOrderManager({
    existingAds,
    newAdPosition,
    onPositionChange,
    darkMode,
}: DisplayOrderManagerProps) {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Create items array with new ad placeholder
    const items = [
        ...existingAds.slice(0, newAdPosition).map((ad) => ({
            id: `existing-${ad.seqId}`,
            title: ad.title,
            isNewAd: false,
        })),
        {
            id: "new-ad",
            title: "새 광고",
            isNewAd: true,
        },
        ...existingAds.slice(newAdPosition).map((ad) => ({
            id: `existing-${ad.seqId}`,
            title: ad.title,
            isNewAd: false,
        })),
    ];

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id && active.id === "new-ad") {
            const oldIndex = items.findIndex((item) => item.id === active.id);
            const newIndex = items.findIndex((item) => item.id === over.id);

            // Use arrayMove to get the new order
            const reorderedItems = arrayMove(items, oldIndex, newIndex);

            // Find the new position of "new-ad" in the reordered array
            const newAdIndexInReordered = reorderedItems.findIndex((item) => item.id === "new-ad");

            // Count how many existing ads are before the new-ad position
            let actualPosition = 0;
            for (let i = 0; i < newAdIndexInReordered; i++) {
                if (reorderedItems[i].id !== "new-ad") {
                    actualPosition++;
                }
            }
            onPositionChange(actualPosition);
        }
    };

    return (
        <MDBox>
            <MDTypography
                variant="caption"
                fontWeight="regular"
                sx={{ color: darkMode ? "rgba(255, 255, 255, 0.8)" : "#344767" }}
                mb={1}
            >
                광고 표시 순서 (드래그하여 위치 조정)
            </MDTypography>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={items.map((item) => item.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {items.map((item) => (
                        <SortableItem
                            key={item.id}
                            id={item.id}
                            title={item.title}
                            isNewAd={item.isNewAd}
                            darkMode={darkMode}
                        />
                    ))}
                </SortableContext>
            </DndContext>
            <MDTypography
                variant="caption"
                sx={{ color: darkMode ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)" }}
                display="block"
                mt={1}
            >
                💡 새 광고를 드래그하여 원하는 위치에 배치하세요
            </MDTypography>
        </MDBox>
    );
}

export default AddInterstitialAdModalClass;
